import { useState } from 'react';
import { Mail, Lock, User, Eye, EyeOff, Sparkles } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Input } from '../ui';
import './LoginForm.css';

export function LoginForm() {
    const { signIn, signUp, signInWithGoogle } = useAuth();
    const [isLogin, setIsLogin] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isGoogleLoading, setIsGoogleLoading] = useState(false);
    const [error, setError] = useState('');

    // Translate Firebase error codes to user-friendly French messages
    const translateFirebaseError = (error: unknown): string => {
        if (!(error instanceof Error)) return 'Une erreur est survenue';

        const errorMessage = error.message.toLowerCase();

        // Authentication errors
        if (errorMessage.includes('auth/invalid-credential') || errorMessage.includes('auth/wrong-password')) {
            return 'Email ou mot de passe incorrect';
        }
        if (errorMessage.includes('auth/user-not-found')) {
            return 'Aucun compte trouvé avec cet email';
        }
        if (errorMessage.includes('auth/email-already-in-use')) {
            return 'Un compte existe déjà avec cet email';
        }
        if (errorMessage.includes('auth/weak-password')) {
            return 'Le mot de passe doit contenir au moins 6 caractères';
        }
        if (errorMessage.includes('auth/invalid-email')) {
            return 'Adresse email invalide';
        }
        if (errorMessage.includes('auth/too-many-requests')) {
            return 'Trop de tentatives. Veuillez réessayer plus tard';
        }
        if (errorMessage.includes('auth/network-request-failed')) {
            return 'Erreur de connexion réseau. Vérifiez votre connexion internet';
        }
        if (errorMessage.includes('auth/popup-closed-by-user')) {
            return 'Connexion annulée';
        }
        if (errorMessage.includes('auth/cancelled-popup-request')) {
            return 'Connexion annulée';
        }
        if (errorMessage.includes('auth/user-disabled')) {
            return 'Ce compte a été désactivé';
        }

        // Default: return a generic message
        return 'Une erreur est survenue. Veuillez réessayer';
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            if (isLogin) {
                await signIn(email, password);
            } else {
                await signUp(email, password, displayName);
            }
            // Force a full page reload to ensure all auth states are properly initialized
            window.location.href = '/';
        } catch (err) {
            setError(translateFirebaseError(err));
            setIsLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        setError('');
        setIsGoogleLoading(true);

        try {
            await signInWithGoogle();
            // Force a full page reload to ensure all auth states are properly initialized
            window.location.href = '/';
        } catch (err) {
            setError(translateFirebaseError(err));
            setIsGoogleLoading(false);
        }
    };

    return (
        <div className="login-container">
            <div className="login-background">
                <div className="login-glow" />
            </div>

            <div className="login-card glass animate-slideUp">
                <div className="login-header">
                    <div className="login-logo">
                        <Sparkles size={32} />
                    </div>
                    <h1 className="login-title">
                        Duc Factory <span className="text-gradient">Tools</span>
                    </h1>
                    <span className="login-company">Portail d'applications internes</span>
                    <p className="login-subtitle">
                        {isLogin ? 'Connectez-vous à votre compte' : 'Créez votre compte'}
                    </p>
                </div>

                {/* Google Sign-In Button */}
                <button
                    type="button"
                    className="google-button"
                    onClick={handleGoogleSignIn}
                    disabled={isGoogleLoading}
                >
                    <svg viewBox="0 0 24 24" width="20" height="20">
                        <path
                            fill="#4285F4"
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                            fill="#34A853"
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                            fill="#FBBC05"
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                            fill="#EA4335"
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                    </svg>
                    {isGoogleLoading ? 'Connexion...' : 'Continuer avec Google'}
                </button>

                <div className="divider">
                    <span>ou</span>
                </div>

                <form onSubmit={handleSubmit} className="login-form">
                    {!isLogin && (
                        <Input
                            label="Nom complet"
                            type="text"
                            placeholder="Jean Dupont"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            leftIcon={<User size={18} />}
                            required
                        />
                    )}

                    <Input
                        label="Email"
                        type="email"
                        placeholder="vous@exemple.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        leftIcon={<Mail size={18} />}
                        required
                    />

                    <Input
                        label="Mot de passe"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        leftIcon={<Lock size={18} />}
                        rightIcon={
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        }
                        required
                    />

                    {error && <div className="login-error">{error}</div>}

                    <Button type="submit" isLoading={isLoading} className="login-button">
                        {isLogin ? 'Se connecter' : 'Créer un compte'}
                    </Button>
                </form>

                <div className="login-footer">
                    <button
                        type="button"
                        className="login-switch"
                        onClick={() => {
                            setIsLogin(!isLogin);
                            setError('');
                        }}
                    >
                        {isLogin
                            ? "Pas encore de compte ? S'inscrire"
                            : 'Déjà un compte ? Se connecter'}
                    </button>
                </div>
            </div>
        </div>
    );
}
